// ----- Phase 5.X-publish-governance — Modal #4: Unplaced forms warning -----
//
// Shown when N admin forms exist without a placement in the eCTD
// section tree. The operator must either go fix the placements
// ("Place now" — closes modal, navigates to the AdminFormsTabPanel)
// or explicitly accept the risk ("Publish anyway" — records the
// acknowledgment in the audit log and continues).

import { type ReactElement } from 'react';
import { ModalShell } from './modalShell';
import {
  buttonRowStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from './modalShellStyles';

export interface UnplacedFormsWarningModalProps {
  readonly unplacedFormsCount: number;
  /** Optional list of form names to surface. Truncated to 5 in the UI. */
  readonly unplacedFormNames?: ReadonlyArray<string>;
  readonly onPublishAnyway: () => void;
  readonly onPlaceNow: () => void;
  readonly onCancel: () => void;
}

export function UnplacedFormsWarningModal({
  unplacedFormsCount,
  unplacedFormNames = [],
  onPublishAnyway,
  onPlaceNow,
  onCancel,
}: UnplacedFormsWarningModalProps): ReactElement {
  const shown = unplacedFormNames.slice(0, 5);
  const remaining = unplacedFormNames.length - shown.length;

  return (
    <ModalShell testId="pgov-unplaced-modal" title="Unplaced admin forms">
      <p style={{ margin: 0, fontSize: 13 }}>
        <strong>
          {unplacedFormsCount} admin form{unplacedFormsCount === 1 ? '' : 's'}
        </strong>{' '}
        ha{unplacedFormsCount === 1 ? 's' : 've'} no placement in the eCTD
        section tree. These forms will not appear in the published package
        unless they're placed.
      </p>
      {shown.length > 0 && (
        <ul
          data-testid="pgov-unplaced-list"
          style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}
        >
          {shown.map((name) => (
            <li key={name}>{name}</li>
          ))}
          {remaining > 0 && <li>…and {remaining} more</li>}
        </ul>
      )}
      <div style={buttonRowStyle}>
        <button
          type="button"
          data-testid="pgov-unplaced-cancel"
          onClick={onCancel}
          style={secondaryButtonStyle}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="pgov-unplaced-place-now"
          onClick={onPlaceNow}
          style={secondaryButtonStyle}
        >
          Place now
        </button>
        <button
          type="button"
          data-testid="pgov-unplaced-publish-anyway"
          onClick={onPublishAnyway}
          style={primaryButtonStyle}
        >
          Publish anyway
        </button>
      </div>
    </ModalShell>
  );
}
